import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import useAuth from "@/hooks/useAuth";
import { fetchLoyaltyTemplates } from "@/redux/slice/loyalty/templeteSlice";
import PointTemplate1 from "@/pages/loyalty/templets/card/points/tempate1";
import PointTemplate2 from "@/pages/loyalty/templets/card/points/template2";
import PointTemplate3 from "@/pages/loyalty/templets/card/points/template3";
import PointTemplate4 from "@/pages/loyalty/templets/card/points/template4";
import GiftCardProgressModal from "../giftCard/GiftCardProgressModal";
import {
    getComponentByTemplate,
    getTemplateForRecord,
    sortTemplatesBySerial,
} from "@/lib/loyalty-template-utils";
import { exportPointBasedTemplatePdf } from "@/lib/export-point-based-template-pdf";
import { printPdfFromUrl, printPdfFromBase64 } from "@/lib/download";
import { printLoyaltyNativeCard } from "@/api/loyaltyNativePrinting";
import { sendPointBasedCardApi } from "@/api/pointBased";

const POINT_TEMPLATE_COMPONENTS = [
    PointTemplate1,
    PointTemplate2,
    PointTemplate3,
    PointTemplate4,
];

const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-GB");
};

const formatReward = (reward, rewardType) => {
    if (reward === null || reward === undefined || reward === "") return null;

    if (rewardType === "percentage") return `${reward}%`;
    if (rewardType === "fixed") return `£${reward}`;
    return String(reward);
};

const getNormalizedPointBased = (selectedPointBased) => {
    const pointBaseCard = selectedPointBased?.point_base_card || selectedPointBased || {};

    const customerName =
        selectedPointBased?.customer_full_name ||
        selectedPointBased?.customer?.full_name ||
        null;

    const previewCode = selectedPointBased?.code || pointBaseCard?.code || null;
    const rewardType = pointBaseCard?.type || selectedPointBased?.type;
    const rawReward = selectedPointBased?.reward ?? pointBaseCard?.reward ?? null;

    return {
        ...pointBaseCard,
        previewCode,
        previewCustomerName: customerName,
        previewRewardLabel: formatReward(rawReward, rewardType),
        previewUsedDate: formatDate(selectedPointBased?.used_at),
        previewUsedPoints: selectedPointBased?.used_points ?? null,
        previewRemainingPoints: selectedPointBased?.remaining_points ?? null,
        previewMinimumRequiredPoints:
            pointBaseCard?.minimum_required_points ?? selectedPointBased?.minimum_required_points ?? null,
        previewEligibleRewardPoints:
            pointBaseCard?.eligible_reward_points ?? selectedPointBased?.eligible_reward_points ?? null,
        previewMessage: pointBaseCard?.template_note || null,
    };
};

const ViewPointBasedReward = ({ isOpen, onClose, selectedVoucher }) => {
    const dispatch = useDispatch();
    const { user } = useAuth();

    const { templates, getLoading } = useSelector(
        (state) => state.loyalty.template,
    );

    useEffect(() => {
        if (!isOpen || !user?.restaurant_id) return;

        dispatch(
            fetchLoyaltyTemplates({
                restaurant_id: user.restaurant_id,
                type: "point",
                per_page: 10,
            }),
        );
    }, [dispatch, isOpen, user?.restaurant_id]);

    const normalizedPointBased = useMemo(
        () => getNormalizedPointBased(selectedVoucher),
        [selectedVoucher],
    );

    const orderedTemplates = sortTemplatesBySerial(templates || []);
    const selectedTemplate =
        getTemplateForRecord(orderedTemplates, normalizedPointBased?.template_id) ||
        orderedTemplates[0] ||
        null;

    const SelectedTemplateComponent =
        getComponentByTemplate(selectedTemplate, POINT_TEMPLATE_COMPONENTS) ||
        POINT_TEMPLATE_COMPONENTS[0];

    const previewData = {
        strictTablePreview: true,
        title: normalizedPointBased?.note,
        customerName: user?.restaurant_name,
        code: normalizedPointBased?.previewCode,
        rewardLabel: normalizedPointBased?.previewRewardLabel,
        eligibleRewardPoints: normalizedPointBased?.previewEligibleRewardPoints,
        minimumRequiredPoints: normalizedPointBased?.previewMinimumRequiredPoints,
        usedPoints: normalizedPointBased?.previewUsedPoints,
        remainingPoints: normalizedPointBased?.previewRemainingPoints,
        usedDate: normalizedPointBased?.previewUsedDate,
        message: normalizedPointBased?.previewMessage,
        isConjunction: normalizedPointBased?.is_conjunction,
        image_url: selectedTemplate?.image_url || selectedTemplate?.image?.file_url || normalizedPointBased?.template?.image_url || normalizedPointBased?.template_image,
        template: selectedTemplate,
    };

    const [isProgressOpen, setIsProgressOpen] = useState(false);
    const [progressData, setProgressData] = useState({ loaded: 0, total: null, percent: null, isCompleted: false });
    const abortControllerRef = useRef(null);

    const handleCancelProgress = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setIsProgressOpen(false);
    };

    const handlePrint = async () => {
        if (!selectedVoucher) return;
        console.log("Selected Point-Based Reward for printing:", selectedVoucher);
        const customerId =
            selectedVoucher?.customer_id ||
            selectedVoucher?.customer?.id ||
            selectedVoucher?.point_base_card_histories?.[0]?.customer_id ||
            selectedVoucher?.point_base_card_histories?.[0]?.customer?.id ||
            null;

        if (selectedVoucher?.template_id && (selectedVoucher?.template_id == 16 || selectedVoucher?.template_id == 8 || selectedVoucher?.template_id == 4)) {
            const nativePrintPayload = {
                restaurant_id: Number(selectedVoucher?.restaurant_id || user?.restaurant_id),
                customer_id: customerId ? Number(customerId) : null,
                channels: ["print"],
                card_type: "point-base-card",
            };
            printLoyaltyNativeCard(selectedVoucher?.restaurant_id || user?.restaurant_id, selectedVoucher?.id, "point-base-card", nativePrintPayload);
            return;
        } 
        else {
            try {
                const cardId = selectedVoucher?.id || selectedVoucher?.point_base_card_id || selectedVoucher?.point_base_card?.id;
                const restaurantId = selectedVoucher?.restaurant_id || user?.restaurant_id;

                if (cardId && restaurantId) {
                    abortControllerRef.current = new AbortController();
                    setProgressData({ loaded: 0, total: null, percent: null, isCompleted: false });
                    setIsProgressOpen(true);

                    const res = await sendPointBasedCardApi(cardId, restaurantId, {
                        signal: abortControllerRef.current.signal,
                        onDownloadProgress: (progressEvent) => {
                            const loaded = progressEvent.loaded || 0;
                            const total = progressEvent.total || null;
                            let percent = null;
                            if (total && total > 0) {
                                percent = Math.round((loaded * 100) / total);
                            }
                            setProgressData({ loaded, total, percent, isCompleted: false });
                        },
                    });

                    if (res?.status === "success" && (res?.data?.pdf_base64 || res?.data?.pdf_url)) {
                        setProgressData((prev) => ({ ...prev, isCompleted: true }));
                        setTimeout(() => {
                            setIsProgressOpen(false);
                            if (res?.data?.pdf_base64) {
                                printPdfFromBase64(res.data.pdf_base64);
                            } else if (res?.data?.pdf_url) {
                                printPdfFromUrl(res.data.pdf_url);
                            }
                            toast.success("Point-based card opened for printing.");
                        }, 600);
                        return;
                    } else {
                        setIsProgressOpen(false);
                    }
                }

                const pdfUrl =
                    selectedVoucher?.pdf_url ||
                    selectedVoucher?.point_base_card?.pdf_url ||
                    selectedVoucher?.point_based_histories?.[0]?.pdf_url ||
                    null;

                if (pdfUrl) {
                    printPdfFromUrl(pdfUrl);
                    toast.success("Point-based reward opened for printing.");
                    return;
                }

                await exportPointBasedTemplatePdf({
                    selectedPointBased: selectedVoucher,
                    templates: orderedTemplates,
                    fileName: `${normalizedPointBased?.previewCode || "point-based"}.pdf`,
                });
                toast.success("Point-based reward exported successfully.");
            } catch (error) {
                setIsProgressOpen(false);
                if (error?.name !== "AbortError") {
                    toast.error(error?.message || "Failed to export point-based reward PDF.");
                }
            }
        }
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
                    <DialogTitle className="text-cwhite">
                        {selectedTemplate?.title || "Point-Based Reward Preview"}
                    </DialogTitle>

                    <div className="flex justify-center items-center">
                        {getLoading ? (
                            <div className="space-y-4 py-3 w-full">
                                <Skeleton className="h-6 w-48" />
                                <Skeleton className="h-[320px] w-full rounded-md" />
                            </div>
                        ) : (
                            <div className="mt-2 w-[546px]">
                                {SelectedTemplateComponent ? (
                                    <SelectedTemplateComponent
                                        template={selectedTemplate}
                                        previewData={previewData}
                                    />
                                ) : (
                                    <div className="text-sm text-cwhite2">
                                        Template preview not available.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end">
                        <Button onClick={handlePrint}>Print</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <GiftCardProgressModal
                isOpen={isProgressOpen}
                progressData={progressData}
                onCancel={handleCancelProgress}
                title="Printing Point Base Card"
                itemType="Point Base Card"
            />
        </>
    );
};

export default ViewPointBasedReward;
